/**
 * Quiz Results Page: Phase 2 Enhancements
 * Ingredient Highlights, Usage Guide, Product Links, Comparison Table
 */

(function() {
  'use strict';

  // Product ingredient and benefit mapping
  const PRODUCT_DETAILS = {
    'Ashwagandha': {
      ingredient: 'Ashwagandha',
      benefit: 'Reduces cortisol and stress levels',
      timing: 'Daytime',
      dosage: 'As directed',
      description: 'Adaptogenic herb that helps manage stress and anxiety throughout the day'
    },
    'Magnesium Glycinate': {
      ingredient: 'Magnesium Glycinate',
      benefit: 'Calms nervous system and promotes relaxation',
      timing: 'Evening',
      dosage: 'Before bed',
      description: 'Highly bioavailable form of magnesium that supports muscle relaxation and sleep quality'
    },
    'Veluna Sleep Formula': {
      ingredient: 'Veluna Sleep Formula',
      benefit: 'Promotes deep, restful sleep',
      timing: 'Evening',
      dosage: '30 minutes before bed',
      description: 'Proprietary blend designed to support natural sleep cycles'
    },
    'Veluna Sleep Strips': {
      ingredient: 'Veluna Sleep Strips',
      benefit: 'Fast-acting sleep support',
      timing: 'As needed',
      dosage: 'Dissolve on tongue',
      description: 'Rapid-acting sublingual strips for immediate sleep support'
    },
    'Ayurvedic Complex': {
      ingredient: 'Ayurvedic Complex',
      benefit: 'All-day stress defense',
      timing: 'Daytime',
      dosage: 'Morning',
      description: 'Traditional Ayurvedic blend for sustained stress management'
    },
    'Recovery Cream': {
      ingredient: 'Recovery Cream',
      benefit: 'Soothes muscle tension',
      timing: 'Evening',
      dosage: 'Apply topically',
      description: 'Topical cream for physical relaxation and recovery'
    },
    'Grass-Fed Collagen Peptides': {
      ingredient: 'Grass-Fed Collagen Peptides',
      benefit: 'Supports recovery and skin health',
      timing: 'Evening',
      dosage: 'Before bed',
      description: 'High-quality collagen for overnight repair and regeneration'
    },
    'Hyaluronic Acid Serum': {
      ingredient: 'Hyaluronic Acid Serum',
      benefit: 'Hydrates and plumps skin',
      timing: 'Evening',
      dosage: 'Apply topically',
      description: 'Topical serum for skin hydration and anti-aging benefits'
    },
    'Sleep Plus Collagen Cream': {
      ingredient: 'Sleep Plus Collagen Cream',
      benefit: 'Overnight skin repair',
      timing: 'Evening',
      dosage: 'Apply before bed',
      description: 'Night cream combining sleep support with skin rejuvenation'
    }
  };

  // Product handle mapping for individual product links
  const PRODUCT_HANDLES_MAP = {
    'Ashwagandha': 'ashwagandha',
    'Magnesium Glycinate': 'magnesium-glycinate',
    'Veluna Sleep Formula': 'veluna-sleep-formula',
    'Veluna Sleep Strips': 'veluna-sleep-strips',
    'Ayurvedic Complex': 'ayurvedic-complex',
    'Recovery Cream': 'recovery-cream',
    'Grass-Fed Collagen Peptides': 'collagen-peptides',
    'Hyaluronic Acid Serum': 'hyaluronic-acid-serum',
    'Sleep Plus Collagen Cream': 'sleep-plus-collagen-cream'
  };

  /**
   * Generate ingredient highlights HTML
   */
  function generateIngredientHighlights(products) {
    if (!products || products.length === 0) return '';

    let html = `
      <div class="veluna-quiz-results-enhancements__ingredients">
        <h3 class="veluna-quiz-results-enhancements__section-title">Key Ingredients & Benefits</h3>
        <div class="veluna-quiz-results-enhancements__ingredients-grid">
    `;

    products.forEach(productName => {
      const details = PRODUCT_DETAILS[productName];
      if (details) {
        html += `
          <div class="veluna-quiz-results-enhancements__ingredient-card">
            <div class="veluna-quiz-results-enhancements__ingredient-header">
              <h4 class="veluna-quiz-results-enhancements__ingredient-name">${details.ingredient}</h4>
              <span class="veluna-quiz-results-enhancements__ingredient-timing">${details.timing}</span>
            </div>
            <p class="veluna-quiz-results-enhancements__ingredient-benefit">
              <strong>${details.benefit}</strong>
            </p>
            <p class="veluna-quiz-results-enhancements__ingredient-description">
              ${details.description}
            </p>
            <div class="veluna-quiz-results-enhancements__ingredient-usage">
              <span class="veluna-quiz-results-enhancements__usage-label">Usage:</span>
              <span class="veluna-quiz-results-enhancements__usage-value">${details.dosage}</span>
            </div>
          </div>
        `;
      }
    });

    html += `
        </div>
      </div>
    `;

    return html;
  }

  /**
   * Generate usage guide HTML
   */
  function generateUsageGuide(category, bundleType, products) {
    const isFullSet = bundleType === 'full-set';
    const productCount = products.length;

    let html = `
      <div class="veluna-quiz-results-enhancements__usage-guide">
        <h3 class="veluna-quiz-results-enhancements__section-title">How to Use Your ${productCount}-Product Set</h3>
        <div class="veluna-quiz-results-enhancements__usage-steps">
    `;

    // Morning/Daytime products
    const morningProducts = products.filter(p => {
      const details = PRODUCT_DETAILS[p];
      return details && details.timing === 'Daytime';
    });

    if (morningProducts.length > 0) {
      html += `
        <div class="veluna-quiz-results-enhancements__usage-step">
          <div class="veluna-quiz-results-enhancements__usage-time">🌅 Morning</div>
          <div class="veluna-quiz-results-enhancements__usage-products">
            ${morningProducts.map(p => {
              const details = PRODUCT_DETAILS[p];
              return `<div class="veluna-quiz-results-enhancements__usage-product">
                <strong>${p}</strong> - ${details?.dosage || 'As directed'}
              </div>`;
            }).join('')}
          </div>
        </div>
      `;
    }

    // Evening products
    const eveningProducts = products.filter(p => {
      const details = PRODUCT_DETAILS[p];
      return details && (details.timing === 'Evening' || details.timing === 'Before bed');
    });

    if (eveningProducts.length > 0) {
      html += `
        <div class="veluna-quiz-results-enhancements__usage-step">
          <div class="veluna-quiz-results-enhancements__usage-time">🌙 Evening</div>
          <div class="veluna-quiz-results-enhancements__usage-products">
            ${eveningProducts.map(p => {
              const details = PRODUCT_DETAILS[p];
              return `<div class="veluna-quiz-results-enhancements__usage-product">
                <strong>${p}</strong> - ${details?.dosage || 'As directed'}
              </div>`;
            }).join('')}
          </div>
        </div>
      `;
    }

    // As needed products
    const asNeededProducts = products.filter(p => {
      const details = PRODUCT_DETAILS[p];
      return details && details.timing === 'As needed';
    });

    if (asNeededProducts.length > 0) {
      html += `
        <div class="veluna-quiz-results-enhancements__usage-step">
          <div class="veluna-quiz-results-enhancements__usage-time">⚡ As Needed</div>
          <div class="veluna-quiz-results-enhancements__usage-products">
            ${asNeededProducts.map(p => {
              const details = PRODUCT_DETAILS[p];
              return `<div class="veluna-quiz-results-enhancements__usage-product">
                <strong>${p}</strong> - ${details?.dosage || 'As directed'}
              </div>`;
            }).join('')}
          </div>
        </div>
      `;
    }

    html += `
          <div class="veluna-quiz-results-enhancements__usage-tip">
            <strong>💡 Pro Tip:</strong> Consistency is key. Use your set daily for 2-3 weeks to see optimal results.
          </div>
        </div>
      </div>
    `;

    return html;
  }

  /**
   * Generate individual product links HTML
   */
  function generateProductLinks(products) {
    if (!products || products.length === 0) return '';

    let html = `
      <div class="veluna-quiz-results-enhancements__product-links">
        <h3 class="veluna-quiz-results-enhancements__section-title">Learn More About Each Product</h3>
        <div class="veluna-quiz-results-enhancements__product-links-grid">
    `;

    products.forEach(productName => {
      const handle = PRODUCT_HANDLES_MAP[productName];
      const details = PRODUCT_DETAILS[productName];
      
      if (handle && details) {
        html += `
          <a href="/products/${handle}" 
             class="veluna-quiz-results-enhancements__product-link"
             target="_blank"
             data-product-link="${productName}">
            <span class="veluna-quiz-results-enhancements__product-link-name">${productName}</span>
            <span class="veluna-quiz-results-enhancements__product-link-arrow">→</span>
          </a>
        `;
      }
    });

    html += `
        </div>
      </div>
    `;

    return html;
  }

  /**
   * Generate comparison table HTML
   */
  function generateComparisonTable(category, starterPricing, fullSetPricing) {
    if (!starterPricing || !fullSetPricing) return '';

    const savings = fullSetPricing.discountedPrice - starterPricing.discountedPrice;
    const additionalProducts = fullSetPricing.productCount - starterPricing.productCount;

    let html = `
      <div class="veluna-quiz-results-enhancements__comparison">
        <h3 class="veluna-quiz-results-enhancements__section-title">Starter Set vs. Full Set Comparison</h3>
        <div class="veluna-quiz-results-enhancements__comparison-table">
          <table>
            <thead>
              <tr>
                <th>Feature</th>
                <th>Starter Set</th>
                <th>Full Set</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Products Included</strong></td>
                <td>${starterPricing.productCount} products</td>
                <td>${fullSetPricing.productCount} products</td>
              </tr>
              <tr>
                <td><strong>Price</strong></td>
                <td>$${starterPricing.discountedPrice}</td>
                <td>$${fullSetPricing.discountedPrice}</td>
              </tr>
              <tr>
                <td><strong>Additional Products</strong></td>
                <td>—</td>
                <td>+${additionalProducts} more products</td>
              </tr>
              <tr>
                <td><strong>Total Savings</strong></td>
                <td>$${starterPricing.savings.toFixed(2)}</td>
                <td>$${fullSetPricing.savings.toFixed(2)}</td>
              </tr>
              <tr>
                <td><strong>Individual Value</strong></td>
                <td>$${starterPricing.individualValue.toFixed(2)}</td>
                <td>$${fullSetPricing.individualValue.toFixed(2)}</td>
              </tr>
              <tr>
                <td><strong>Best For</strong></td>
                <td>Getting started</td>
                <td>Complete support</td>
              </tr>
            </tbody>
          </table>
          <div class="veluna-quiz-results-enhancements__comparison-cta">
            <p class="veluna-quiz-results-enhancements__comparison-text">
              Upgrade to Full Set and save an additional $${savings} while getting ${additionalProducts} more products for complete support.
            </p>
          </div>
        </div>
      </div>
    `;

    return html;
  }

  // Category-specific customer reviews
  const CATEGORY_REVIEWS = {
      // Placeholder testimonials removed 2026-09-10: no verifiable purchasers (internal compliance review).
    };

  // FAQ data
  const FAQ_DATA = [
    {
      question: "How long until I see results?",
      answer: "Most customers notice improvements in sleep quality within the first week. Optimal results typically appear after 2-3 weeks of consistent use. Individual results may vary."
    },
    {
      question: "Is this safe to use every night?",
      answer: "Yes, all Veluna products are formulated with natural ingredients and are safe for nightly use. However, we recommend consulting with your healthcare provider if you have any medical conditions or are taking medications."
    },
    {
      question: "What if I'm not satisfied?",
      answer: "We offer a 30-night money-back guarantee. If you're not completely satisfied with your results, contact us within 30 days for a full refund."
    },
    {
      question: "Do you offer free shipping?",
      answer: "Yes! All ritual sets include free shipping. Individual products qualify for free shipping on orders over $50."
    },
    {
      question: "Can I subscribe and save?",
      answer: "Yes! Subscribe to any set and save 20% on every order. You can cancel or modify your subscription at any time."
    },
    {
      question: "Are Veluna products third-party tested?",
      answer: "Yes. Every Veluna supplement is third-party tested and made in an FDA-registered, third-party GMP-certified facility (NSF or UL). Certificates of Analysis are available for every product on request, and every supplement lists its full Supplement Facts on its product page."
    },
    {
      question: "How do I know which set is right for me?",
      answer: "Our quiz analyzes your sleep challenges, routine, and goals to recommend the perfect set for your needs. You can also chat with Luna, our AI assistant, for personalized recommendations."
    },
    {
      question: "What's the difference between Starter, Booster, and Full Sets?",
      answer: "Starter Sets include 2 essential products for getting started. Booster Sets include 2 products that enhance your existing routine. Full Sets include all 4 products for complete, comprehensive support."
    }
  ];

  /**
   * Generate customer reviews HTML
   */
  function generateCustomerReviews(category) {
    const reviews = CATEGORY_REVIEWS[category] || CATEGORY_REVIEWS['mind-calm'];
    
    let html = `
      <div class="veluna-quiz-results-enhancements__reviews">
        <h3 class="veluna-quiz-results-enhancements__section-title">What Customers Are Saying</h3>
        <div class="veluna-quiz-results-enhancements__reviews-stats">
          <div class="veluna-quiz-results-enhancements__reviews-rating">
            <span class="veluna-quiz-results-enhancements__reviews-stars">★★★★★</span>
            <span class="veluna-quiz-results-enhancements__reviews-score">4.8/5</span>
          </div>
          <div class="veluna-quiz-results-enhancements__reviews-count">Based on 1,234+ reviews</div>
        </div>
        <div class="veluna-quiz-results-enhancements__reviews-grid">
    `;

    reviews.forEach(review => {
      const stars = '★'.repeat(review.rating) + '☆'.repeat(5 - review.rating);
      html += `
        <div class="veluna-quiz-results-enhancements__review-card">
          <div class="veluna-quiz-results-enhancements__review-header">
            <div class="veluna-quiz-results-enhancements__review-stars">${stars}</div>
            ${review.verified ? '<span class="veluna-quiz-results-enhancements__review-verified">✓ Verified Purchase</span>' : ''}
          </div>
          <p class="veluna-quiz-results-enhancements__review-quote">"${review.quote}"</p>
          <div class="veluna-quiz-results-enhancements__review-author">
            <strong>${review.author}</strong>
            <span>${review.location}</span>
          </div>
        </div>
      `;
    });

    html += `
        </div>
      </div>
    `;

    return html;
  }

  /**
   * Generate FAQ section HTML
   */
  function generateFAQ() {
    let html = `
      <div class="veluna-quiz-results-enhancements__faq">
        <h3 class="veluna-quiz-results-enhancements__section-title">Frequently Asked Questions</h3>
        <div class="veluna-quiz-results-enhancements__faq-list">
    `;

    FAQ_DATA.forEach((faq, index) => {
      html += `
        <div class="veluna-quiz-results-enhancements__faq-item">
          <button class="veluna-quiz-results-enhancements__faq-question" 
                  aria-expanded="false"
                  data-faq-toggle="${index}">
            <span>${faq.question}</span>
            <span class="veluna-quiz-results-enhancements__faq-icon">+</span>
          </button>
          <div class="veluna-quiz-results-enhancements__faq-answer" 
               data-faq-answer="${index}">
            <p>${faq.answer}</p>
          </div>
        </div>
      `;
    });

    html += `
          <div class="veluna-quiz-results-enhancements__faq-cta">
            <p>Still have questions? <a href="#" onclick="window.Luna?.openChatbot(); return false;">Chat with Luna</a> for instant answers.</p>
          </div>
        </div>
      </div>
    `;

    return html;
  }

  /**
   * Generate subscription option HTML
   */
  function generateSubscriptionOption(category, bundle, price) {
    let html = `
      <div class="veluna-quiz-results-enhancements__subscription">
        <div class="veluna-quiz-results-enhancements__subscription-header">
          <h3 class="veluna-quiz-results-enhancements__subscription-title">Subscribe & Save 20%</h3>
          <span class="veluna-quiz-results-enhancements__subscription-badge">Most Popular</span>
        </div>
        <div class="veluna-quiz-results-enhancements__subscription-content">
          <div class="veluna-quiz-results-enhancements__subscription-benefits">
            <div class="veluna-quiz-results-enhancements__subscription-benefit">
              <span class="veluna-quiz-results-enhancements__subscription-check">✓</span>
              <span>Save 20% on every order</span>
            </div>
            <div class="veluna-quiz-results-enhancements__subscription-benefit">
              <span class="veluna-quiz-results-enhancements__subscription-check">✓</span>
              <span>Free shipping always</span>
            </div>
            <div class="veluna-quiz-results-enhancements__subscription-benefit">
              <span class="veluna-quiz-results-enhancements__subscription-check">✓</span>
              <span>Cancel or modify anytime</span>
            </div>
            <div class="veluna-quiz-results-enhancements__subscription-benefit">
              <span class="veluna-quiz-results-enhancements__subscription-check">✓</span>
              <span>Priority customer support</span>
            </div>
          </div>
          <div class="veluna-quiz-results-enhancements__subscription-pricing">
            <div class="veluna-quiz-results-enhancements__subscription-price-row">
              <span>One-time:</span>
              <span>$${price}</span>
            </div>
            <div class="veluna-quiz-results-enhancements__subscription-price-row veluna-quiz-results-enhancements__subscription-price-row--discounted">
              <span>Subscribe:</span>
              <span>$${(price * 0.8).toFixed(0)} <small>/month</small></span>
            </div>
            <div class="veluna-quiz-results-enhancements__subscription-savings">
              Save $${(price * 0.2).toFixed(0)} per month
            </div>
          </div>
          <div class="veluna-quiz-results-enhancements__subscription-frequency">
            <label>Delivery Frequency:</label>
            <select class="veluna-quiz-results-enhancements__subscription-select" data-subscription-frequency>
              <option value="30">Every 30 days</option>
              <option value="60">Every 60 days</option>
              <option value="90">Every 90 days</option>
            </select>
          </div>
          <button class="veluna-btn veluna-btn-primary veluna-quiz-results-enhancements__subscription-btn" 
                  data-subscribe-to-cart
                  data-category="${category}"
                  data-bundle="${bundle}">
            Subscribe & Save 20%
          </button>
          <p class="veluna-quiz-results-enhancements__subscription-note">
            You can cancel or modify your subscription at any time from the Veluna subscriber manage page.
          </p>
        </div>
      </div>
    `;

    return html;
  }

  /**
   * Enhance quiz results with Phase 2 features
   */
  function enhanceQuizResults() {
    const resultsContainer = document.querySelector('[data-quiz-results-optimal]');
    if (!resultsContainer) {
      // Retry after 100ms
      setTimeout(enhanceQuizResults, 100);
      return;
    }

    // Get quiz results data
    const urlParams = new URLSearchParams(window.location.search);
    const category = urlParams.get('category');
    const bundle = urlParams.get('bundle');

    if (!category || !bundle) {
      console.warn('Quiz Results Enhancements: Missing category or bundle');
      return;
    }

    // Get pricing data
    const pricing = window.VelunaQuizResultsOptimal?.VERIFIED_PRICING?.[category]?.[bundle.toLowerCase().replace(/\s+/g, '-')];
    const fullSetPricing = window.VelunaQuizResultsOptimal?.VERIFIED_PRICING?.[category]?.['full-set'];
    const starterPricing = window.VelunaQuizResultsOptimal?.VERIFIED_PRICING?.[category]?.['starter-set'];

    if (!pricing) {
      console.warn('Quiz Results Enhancements: Pricing data not found');
      return;
    }

    // Create enhancements container
    let enhancementsHTML = '<div class="veluna-quiz-results-enhancements">';

    // Add ingredient highlights
    enhancementsHTML += generateIngredientHighlights(pricing.products);

    // Add usage guide
    enhancementsHTML += generateUsageGuide(category, bundle, pricing.products);

    // Add individual product links
    enhancementsHTML += generateProductLinks(pricing.products);

    // Add comparison table (only if Starter or Booster)
    if (bundle !== 'full-set' && starterPricing && fullSetPricing) {
      enhancementsHTML += generateComparisonTable(category, starterPricing, fullSetPricing);
    }

    // Add customer reviews
    enhancementsHTML += generateCustomerReviews(category);

    // Add FAQ section
    enhancementsHTML += generateFAQ();

    // Add subscription option
    enhancementsHTML += generateSubscriptionOption(category, bundle, pricing.discountedPrice);

    enhancementsHTML += '</div>';

    // Insert after the main results container
    resultsContainer.insertAdjacentHTML('afterend', enhancementsHTML);

    // Track product link clicks
    const productLinks = document.querySelectorAll('[data-product-link]');
    productLinks.forEach(link => {
      link.addEventListener('click', function() {
        const productName = this.dataset.productLink;
        if (window.VelunaQuizResultsCart && window.VelunaQuizResultsCart.trackEvent) {
          window.VelunaQuizResultsCart.trackEvent('product_link_click', {
            product_name: productName,
            category: category,
            bundle: bundle
          });
        }
      });
    });

    // Initialize FAQ accordion
    const faqQuestions = document.querySelectorAll('[data-faq-toggle]');
    faqQuestions.forEach(question => {
      question.addEventListener('click', function() {
        const index = this.dataset.faqToggle;
        const answer = document.querySelector(`[data-faq-answer="${index}"]`);
        const icon = this.querySelector('.veluna-quiz-results-enhancements__faq-icon');
        const isExpanded = this.getAttribute('aria-expanded') === 'true';

        // Close all other FAQs
        faqQuestions.forEach(q => {
          if (q !== this) {
            q.setAttribute('aria-expanded', 'false');
            const otherIndex = q.dataset.faqToggle;
            const otherAnswer = document.querySelector(`[data-faq-answer="${otherIndex}"]`);
            const otherIcon = q.querySelector('.veluna-quiz-results-enhancements__faq-icon');
            if (otherAnswer) otherAnswer.classList.remove('veluna-quiz-results-enhancements__faq-answer--open');
            if (otherIcon) otherIcon.textContent = '+';
          }
        });

        // Toggle current FAQ
        this.setAttribute('aria-expanded', !isExpanded);
        if (answer) {
          answer.classList.toggle('veluna-quiz-results-enhancements__faq-answer--open');
        }
        if (icon) {
          icon.textContent = !isExpanded ? '−' : '+';
        }
      });
    });

    // Initialize subscription button
    const subscribeBtn = document.querySelector('[data-subscribe-to-cart]');
    if (subscribeBtn) {
      subscribeBtn.addEventListener('click', function() {
        const category = this.dataset.category;
        const bundle = this.dataset.bundle;
        const frequency = document.querySelector('[data-subscription-frequency]')?.value || '30';

        // Track subscription click
        if (window.VelunaQuizResultsCart && window.VelunaQuizResultsCart.trackEvent) {
          window.VelunaQuizResultsCart.trackEvent('subscription_click', {
            category: category,
            bundle: bundle,
            frequency: frequency
          });
        }

        // For now, add to cart with subscription metadata
        // TODO: Implement actual subscription functionality
        console.log('Subscription clicked:', { category, bundle, frequency });
        alert('Subscription functionality will be implemented. For now, please add to cart and we\'ll set up your subscription.');
      });
    }

    console.log('Quiz Results Enhancements: Phase 2 features added');
  }

  /**
   * Initialize
   */
  function init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
      return;
    }

    // Wait for quiz results to be rendered
    function waitForResults() {
      const resultsContainer = document.querySelector('[data-quiz-results-optimal]');
      if (resultsContainer && resultsContainer.innerHTML.trim()) {
        enhanceQuizResults();
      } else {
        setTimeout(waitForResults, 100);
      }
    }

    waitForResults();
  }

  // Initialize
  init();

  // Export for external use
  if (typeof window !== 'undefined') {
    window.VelunaQuizResultsEnhancements = {
      generateIngredientHighlights,
      generateUsageGuide,
      generateProductLinks,
      generateComparisonTable,
      PRODUCT_DETAILS,
      PRODUCT_HANDLES_MAP
    };
  }
})();

